import React, { useState } from 'react'
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Paper,
  Chip,
  IconButton
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  FolderOpen as ExplorerIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import { useLanguage } from '../../contexts/LanguageContext'
import ExplorerModal from './ExplorerModal'

export type ImageSource = 'last_generated' | 'upload' | 'explorer'

interface ImageSourceSelectorProps {
  selectedSource: ImageSource
  onSourceChange: (source: ImageSource) => void
  selectedImageUrl: string | null
  onImageSelect: (url: string) => void
  onFileSelect?: (file: File | null) => void
  lastGeneratedUrl?: string | null
  disabled?: boolean
}

const ImageSourceSelector: React.FC<ImageSourceSelectorProps> = ({
  selectedSource,
  onSourceChange,
  selectedImageUrl,
  onImageSelect,
  onFileSelect,
  lastGeneratedUrl,
  disabled = false
}) => {
  const { t } = useLanguage()
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [explorerOpen, setExplorerOpen] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadFile(file)
      // Create local URL for preview
      const url = URL.createObjectURL(file)
      onImageSelect(url)
      // Pass file to parent component
      onFileSelect?.(file)
    }
  }

  const handleExplorerSelect = (url: string) => {
    onImageSelect(url)
    setExplorerOpen(false)
  }

  const handleClearSelection = () => {
    onImageSelect('')
    setUploadFile(null)
    onFileSelect?.(null)
  }

  const getSelectedFileName = () => {
    if (!selectedImageUrl) return null

    if (selectedSource === 'upload' && uploadFile) {
      return uploadFile.name
    }

    if (selectedSource === 'explorer') {
      return selectedImageUrl.split('/').pop() || 'selected image'
    }

    if (selectedSource === 'last_generated') {
      return t('easyWay.imageSource.lastGenerated')
    }

    return null
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {t('easyWay.imageSource.title')}
      </Typography>

      <RadioGroup
        value={selectedSource}
        onChange={(e) => onSourceChange(e.target.value as ImageSource)}
      >
        <FormControlLabel
          value="last_generated"
          control={<Radio />}
          label={t('easyWay.imageSource.lastGenerated')}
          disabled={disabled || !lastGeneratedUrl}
        />

        <FormControlLabel
          value="upload"
          control={<Radio />}
          label={t('easyWay.imageSource.upload')}
          disabled={disabled}
        />

        <FormControlLabel
          value="explorer"
          control={<Radio />}
          label={t('easyWay.imageSource.explorer')}
          disabled={disabled}
        />
      </RadioGroup>

      {/* Upload file button */}
      {selectedSource === 'upload' && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={disabled}
            fullWidth
          >
            {uploadFile ? uploadFile.name : t('easyWay.imageSource.selectFile')}
            <input
              type="file"
              hidden
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleFileChange}
            />
          </Button>
        </Box>
      )}

      {/* Explorer button */}
      {selectedSource === 'explorer' && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ExplorerIcon />}
            onClick={() => setExplorerOpen(true)}
            disabled={disabled}
            fullWidth
          >
            {t('easyWay.imageSource.openExplorer')}
          </Button>
        </Box>
      )}

      {/* Selected image display */}
      {selectedImageUrl && (
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('easyWay.imageSource.selected')}:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {getSelectedFileName()}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClearSelection}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Explorer Modal */}
      <ExplorerModal
        open={explorerOpen}
        onClose={() => setExplorerOpen(false)}
        onSelect={handleExplorerSelect}
      />
    </Paper>
  )
}

export default ImageSourceSelector
